import { OrgIndexRedirectPage } from "./index-redirect"
import { Capacitor } from "@capacitor/core"
import { useEffect } from "react"
import { Navigate, useLocation, useParams } from "react-router-dom"
import { FullScreen } from "~/components/screens/FullScreen"
import { useDiscordActivity } from "~/components/providers/DiscordActivity"
import { Button } from "~/components/ui/button"
import { LoadingState } from "~/components/screens/StateScreen"
import { authClient } from "~/lib/auth-client"
import { isUlid } from "~/lib/ulid"
import { api } from "~/trpc/react"
import InvitePage from "../invite/page"
import Layout from "../main/layout"
import { CustomSuggestionDetailOrNothing } from "../suggestions/custom-domain"

export default function OrgEntryPage() {
	const { orgSlug = "" } = useParams<{ orgSlug: string }>()
	const location = useLocation()
	const { isActivity } = useDiscordActivity()
	const { data: session, isPending } = authClient.useSession()
	const domain = api.meta.customDomain.useQuery(undefined, { retry: false })
	const customDomain = api.suggestions.publicBoard.useQuery(
		{ host: window.location.host },
		{ retry: false }
	)
	const orgs = api.user.getOrgs.useQuery(undefined, {
		enabled:
			!!session &&
			domain.isSuccess &&
			customDomain.isSuccess &&
			!customDomain.data
	})
	const hasDomainAccess =
		orgs.data?.some((org) => org.slug === domain.data?.slug) ?? false
	const otherOrganization = !!domain.data && domain.data.slug !== orgSlug
	const selectedOrganization = orgs.data?.find((org) => org.slug === orgSlug)
	const canonicalDomain =
		!Capacitor.isNativePlatform() &&
		!isActivity &&
		selectedOrganization?.customDomainVerifiedAt
			? selectedOrganization.customDomain
			: null
	const needsCanonicalDomain =
		!!canonicalDomain && window.location.hostname !== canonicalDomain

	useEffect(() => {
		if (
			domain.data &&
			(isPending || !session || !orgs.isSuccess || !hasDomainAccess)
		)
			return
		if (!otherOrganization && !needsCanonicalDomain) return
		const base = needsCanonicalDomain
			? `https://${canonicalDomain}`
			: domain.data?.baseUrl
		if (base)
			window.location.replace(
				new URL(
					`${location.pathname}${location.search}${location.hash}`,
					base
				).toString()
			)
	}, [
		domain.data,
		isPending,
		session,
		orgs.isSuccess,
		hasDomainAccess,
		otherOrganization,
		needsCanonicalDomain,
		canonicalDomain,
		domain.data?.baseUrl,
		location.pathname,
		location.search,
		location.hash
	])

	if (customDomain.isLoading || domain.isLoading) return <LoadingState />
	if (domain.isError)
		return (
			<FullScreen
				title="Unable to load workspace"
				message="Please try again later."
			/>
		)
	if (customDomain.data) return <CustomSuggestionDetailOrNothing />
	if (domain.data) {
		if (isPending) return <LoadingState />
		if (!session)
			return (
				<Navigate
					to={`/login?redirect_uri=${encodeURIComponent(`${location.pathname}${location.search}${location.hash}`)}`}
					replace
				/>
			)
		if (orgs.isLoading) return <LoadingState />
		if (orgs.isError)
			return (
				<FullScreen
					title="Unable to load workspace"
					message="Please try again later."
				/>
			)
		if (!hasDomainAccess) {
			return (
				<FullScreen
					title="Workspace access required"
					message={`You are not a member of ${domain.data.name}. Contact an organization administrator for access.`}
					bottomContent={
						<Button asChild variant="outline">
							<a href={domain.data.baseUrl}>My Account</a>
						</Button>
					}
				/>
			)
		}
		if (otherOrganization) return <LoadingState />
	}
	if (needsCanonicalDomain || (session && orgs.isLoading))
		return <LoadingState />
	if (isUlid(orgSlug)) return <InvitePage />
	if (location.pathname.replace(/\/$/, "") === `/${orgSlug}`)
		return <OrgIndexRedirectPage />
	return <Layout />
}
